/**
 * Team power / HP — bot parity with `utils/combat.ts` (team section).
 */
import { effectRates, getItem } from '../data/index.js';
import type { OwnedItem, SummonedCategory, TeamSlot } from '../state.js';
import type { ItemType } from '../types.js';
import { elementDamage, type StatCarrier } from './elements.js';

export interface TeamCarrier extends StatCarrier {
  summoned: Record<ItemType, SummonedCategory> & { team: { equiped: TeamSlot[] } };
}

export interface PowerItem {
  tier: number;
  level: number;
  element?: string;
  type?: ItemType;
}

export function findOwned(user: TeamCarrier, type: ItemType, id: number): OwnedItem | undefined {
  return user.summoned[type].owned.find((o) => o.id === id);
}

/** Collection bonus in % for a type: Σ owned `level · effectRates[tier-1]` (`getTypeOwnedEffect`). */
export function typeOwnedEffect(user: TeamCarrier, type: ItemType): number {
  return user.summoned[type].owned
    .filter((o) => o.total > 0)
    .reduce((acc, o) => {
      const item = getItem(type, o.id);
      if (!item) return acc;
      return acc + o.level * (effectRates[item.tier - 1] ?? 0);
    }, 0);
}

/** Bonus contributed by one owned item now and after one more level (`getOwnedEffect`). */
export function ownedEffect(type: ItemType, tier: number, level: number): { current: number; next: number; stat: 'attack' | 'hp' } {
  const rate = effectRates[tier - 1] ?? 0;
  return { current: level * rate, next: (level + 1) * rate, stat: type === 'shields' || type === 'pets' ? 'hp' : 'attack' };
}

/** `tier^5 · log10(level + tier) · (10 + 10·tier)` × element × collection bonus (`itemPowerFormula`). */
export function itemPower(item: PowerItem, user: TeamCarrier): number {
  if (!item.tier) return 0;
  let result = Math.pow(item.tier, 5) * Math.log10(item.level + item.tier) * (10 + item.tier * 10);
  if (item.element) result = elementDamage(item.element, user, result);
  const multiplicator = item.type ? 1 + typeOwnedEffect(user, item.type) / 100 : 1;
  return Number.isNaN(result) ? 0 : result * multiplicator;
}

/** `mergeItemData`: static tier/element + the player's level for that item (default 1). */
export function powerItem(user: TeamCarrier, type: ItemType, id: number): PowerItem {
  const item = getItem(type, id);
  return {
    tier: item?.tier ?? 0,
    level: findOwned(user, type, id)?.level ?? 1,
    element: item?.element,
    type,
  };
}

/** Σ of item powers, floored (`getItemsPower`). */
export function itemsPower(list: PowerItem[], user: TeamCarrier): number {
  if (list.length === 0) return 0;
  return Math.floor(list.reduce((acc, i) => acc + itemPower(i, user), 0));
}

export function slotAttack(user: TeamCarrier, slot: TeamSlot): number {
  return itemsPower([powerItem(user, 'characters', slot.characters), powerItem(user, 'weapons', slot.weapons)], user);
}

export function slotHp(user: TeamCarrier, slot: TeamSlot): number {
  return itemsPower([powerItem(user, 'shields', slot.shields), powerItem(user, 'pets', slot.pets)], user);
}

/** `getTeamPower`: `level·10` + characters & weapons of every slot. */
export function teamPower(user: TeamCarrier): number {
  let power = user.level.level * 10;
  for (const slot of user.summoned.team.equiped) power += slotAttack(user, slot);
  return Math.floor(power);
}

/** `getTeamHp`: `(level·300 + shields & pets) · 10.24`. */
export function teamHp(user: TeamCarrier): number {
  let power = 0;
  for (const slot of user.summoned.team.equiped) power += slotHp(user, slot);
  return Math.floor((user.level.level * 300 + power) * 10.24);
}

/**
 * A bot shadow of `user` (`getTeamPower`/`getTeamHp` with `isABot` + `botRatio`):
 * power is scaled as a whole, HP only on its shield/pet part.
 */
export function shadowPower(user: TeamCarrier, ratio: number): number {
  let power = user.level.level * 10;
  for (const slot of user.summoned.team.equiped) power += slotAttack(user, slot);
  return Math.floor(power * ratio);
}

export function shadowHp(user: TeamCarrier, ratio: number): number {
  let power = 0;
  for (const slot of user.summoned.team.equiped) power += slotHp(user, slot);
  return Math.floor((user.level.level * 300 + power * ratio) * 10.24);
}

/** `getUserPower`: single "power" figure shown on profiles. */
export function userPower(user: TeamCarrier): number {
  return Math.floor(teamHp(user) / 100 + teamPower(user));
}
